<template>
  <Stepper
    :v-if="!loading"
    :stepInfo="stepInfo()"
    :description="currentStepDescription"
  />
</template>

<script>
import { mapState } from 'vuex'
import Stepper from '@/components/requests/Stepper'
import SurveyResponse from '@/services/surveyresponses'

export default {
  name: 'RequestStepProgressBar',
  components: {
    Stepper
  },
  props: {
    isPaypalVerify: {
      type: Boolean,
    },
    isEventCategories: {
      type: Boolean,
    },
    isCreateEvents: {
      type: Boolean,
    },
    isEventDocumentation: {
      type: Boolean,
    },
    isExpenseCategories: {
      type: Boolean,
    },
    isExpenseCategoriesAlt: {
      type: Boolean,
    },
    isExpenseDocumentation: {
      type: Boolean,
    },
    isHouseholdDetailsSurvey: {
      type: Boolean,
    },
    isFinancialHealthSurvey: {
      type: Boolean,
    },
    isRequestSummary: {
      type: Boolean,
    },
    isExpenseDocNeeded: {
      type: Boolean,
      default: true
    },
  },
  data() {
    return {
      isHouseholdDetailsSurveyEnabled: false,
      hasCompletedHouseholdDetailsSurvey: false,
      isFinancialHealthSurveyEnabled: false,
      hasCompletedFinancialHealthSurvey: false,
      loading: false,
    }
  },
  async created() {
    this.loading = true
    this.isHouseholdDetailsSurveyEnabled = this.organizationDetails.isHouseholdDetailsSurveyEnabled
    this.isFinancialHealthSurveyEnabled = this.organizationDetails.isFinancialHealthSurveyEnabled
    this.loading = false

    if (this.isPaypalVerify) {
      const hasCompletedHouseholdDetailsSurvey = await SurveyResponse.checkIfEmployeeHasCompleted(
        this.user,
        'Household Details',
      )

      const hasCompletedFinancialHealthSurvey = await SurveyResponse.checkIfEmployeeHasCompleted(
        this.user,
        'Financial Health',
      )

      this.$store.dispatch('updateHasCompletedFinHealthSurvey', hasCompletedFinancialHealthSurvey)
      this.$store.dispatch('updateHasCompletedHouseholdSurvey', hasCompletedHouseholdDetailsSurvey)
    }

  },
  methods: {
    stepCountPrediction(startNumber) {
      // this number changes with
      // 1. surveys off/on and complete or not,
      // 2. event selected
      var steps = startNumber

      if ((!this.hasCompletedFinHealthSurveyStart || this.hasCompletedFinancialHealthSurvey) && this.isFinancialHealthSurveyEnabled) {
          steps = steps + 1
      }
      if ((!this.hasCompletedHouseholdSurveyStart || this.hasCompletedHouseholdDetailsSurvey) && this.isHouseholdDetailsSurveyEnabled) {
          steps = steps + 1
      }

      if (!this.currentRequest.requiresExpenseDocs) {
          steps = steps - 1
      }

      return steps
    },
    currentHouseholdSurveysStepNumber(startNumber) {
      // this number changes with
      // 1. surveys off/on and complete or not,
      // 2. event selected

      var steps = startNumber

      if (!this.hasCompletedFinHealthSurveyStart && !this.isFinancialHealthSurveyEnabled) {
          steps = steps - 1
      }

      if (!this.currentRequest.requiresExpenseDocs) {
          steps = steps - 1
      }

      return steps
    },
    currentFinHealthSurveysStepNumber(startNumber) {
      // this number changes with
      // 1. surveys off/on and complete or not,
      // 2. event selected

      var steps = startNumber

      if (!this.hasCompletedHouseholdSurveyStart && !this.isHouseholdDetailsSurveyEnabled) {
          steps = steps - 1
      }

      if (!this.currentRequest.requiresExpenseDocs) {
          steps = steps - 1
      }

      return steps
    },
    currentStepNumber() {
      if(this.isPaypalVerify) {
        return 1
      } else if(this.isEventCategories) {
        return 2
      } else if(this.isCreateEvents) {
        return 3
      } else if(this.isEventDocumentation) {
        return 4
      } else if(this.isExpenseCategories || this.isExpenseCategoriesAlt) {
        return 5
      } else if(this.isExpenseDocumentation) {
        return 6
      } else if(this.isHouseholdDetailsSurvey) {
        /*# either 7 or 6*/
        return this.currentHouseholdSurveysStepNumber(7)
      } else if(this.isFinancialHealthSurvey) {
        /*# either 7 or 8*/
        return this.currentFinHealthSurveysStepNumber(8)
      } else if(this.isRequestSummary) {
        /*# either 6, 7, 8 or 9*/
        return this.stepCountPrediction(7)
      }
    },
    stepInfo() {
      let msg = this.$gettext('Step %{currentStepNumber} of %{stepCountPrediction}');
      const placeholders = {
        currentStepNumber: this.currentStepNumber(),
        stepCountPrediction: this.stepCountPrediction(7)
      }
      return this.$gettextInterpolate(msg, placeholders);
    },
  },

  computed: {
    ...mapState(['user', 'organizationDetails', 'currentRequest', 'hasCompletedFinHealthSurveyStart', 'hasCompletedHouseholdSurveyStart']),
    currentStepDescription() {
      if(this.isPaypalVerify) {
        return this.$gettext("Make sure you can receive money")
      } else if(this.isEventCategories) {
        return this.$gettext("Describe your situation")
      } else if(this.isCreateEvents) {
        return this.$gettext("Add details")
      } else if(this.isEventDocumentation) {
        return this.$gettext("Document your emergency")
      } else if(this.isExpenseCategories) {
        return this.$gettext("Document expenses and amounts needed")
      } else if(this.isExpenseCategoriesAlt) {
        return this.$gettext("Describe expenses and amounts needed")
      } else if(this.isExpenseDocumentation) {
        return this.$gettext("Document your expenses")
      } else if(this.isHouseholdDetailsSurvey) {
        return this.$gettext("Household Details Survey")
      } else if(this.isFinancialHealthSurvey) {
        return this.$gettext("Financial Health Survey")
      } else if(this.isRequestSummary) {
        return this.$gettext("Request Summary")
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/styles/mixins/responsive';

.step-progress-bar {
  display: flex;
  justify-content: center;

  .step-wrapper {
    @include tablet-and-up-screen {
      max-width: 70%;
    }
  }

  .step-number-text {
    font-size: 1.25rem;
    line-height: 1.75rem;

    @include desktop-screen {
      font-size: 1.5rem;
      line-height: 2rem;
    }
  }

  .step-description-text {
    font-size: 2rem;
    font-weight: bold;
    line-height: 2.5rem;

    @include tablet-screen {
      font-size: 2.25rem;
      line-height: 2.8rem;
    }

    @include desktop-screen {
      font-size: 3rem;
      line-height: 3.75rem;
    }
  }
}
</style>
